/**
 * Calculates the next execution times based on the specified mode and time parameters.
 * @param {string} mode - The execution mode ('yearly', 'monthly', or 'daily').
 * @param {number} count - The number of execution times to calculate.
 * @param {number} month - The month value (0-11). Used in yearly mode.
 * @param {number} day - The day of the month value (1-31). Used in yearly and monthly modes.
 * @param {number} hour - The hour value (0-23). Used in yearly, monthly, and daily modes.
 * @param {number} minute - The minute value (0-59). Used in yearly, monthly, and daily modes.
 * @returns {Date[]} An array of Date objects representing the next execution times.
 * @throws {Error} If an invalid mode is provided.
 */
export function calculateNextExecutionTimes(mode, count, month, day, hour, minute) {
  // Determine the calculation based on the specified mode
  switch (mode) {
    case 'yearly':
      // Calculate yearly execution times
      return calculateYearlyExecutionTimes(month, day, hour, minute, count);
    case 'monthly':
      // Calculate monthly execution times
      return calculateMonthlyExecutionTimes(day, hour, minute, count);
    case 'daily':
      // Calculate daily execution times
      return calculateDailyExecutionTimes(hour, minute, count);
    case 'hourly':
      // Calculate hourly execution times
      return calculateHourlyExecutionTimes(minute, count);
    default:
      // Throw an error for an invalid mode
      throw new Error('Invalid mode. Must be one of: yearly, monthly, daily.');
  }
}

/**
 * Calculates the next yearly execution times based on the provided month, day, hour, minute, and count.
 * @param {number} _month - The month value
 * @param {number} _day - The day of month value
 * @param {number} _hour - The hour value
 * @param {number} _minute - The minute value
 * @param {number} count - The number of execution times to calculate.
 * @returns {Date[]} An array of Date objects representing the next execution times.
 * @throws {Error} If the day is invalid for February (must be between 1 and 29).
 */
export function calculateYearlyExecutionTimes(_month, _day, _hour, _minute, count) {
  // Sanitize the input arguments to ensure valid values are used
  const [minute, hour, day, month] = sanitizeArgs(_minute, _hour, _day, _month);

  // Check if the provided day is valid for February
  if (month === 2 && day > 29) {
    throw new Error('Invalid day for February, must be between 1 and 29.');
  }

  // Array to store the next execution times
  const nextExecutionTimes = [];

  // Get the current date and time
  const now = new Date(Date.now());

  // Get the current year
  let year = now.getFullYear();

  // Calculate the next execution times
  while (nextExecutionTimes.length < count) {
    // Create a new Date object based on the provided values
    const date = new Date(year, month - 1, day, hour, minute);

    // Check if the calculated date is in the future and matches the asked date
    if (date >= now && isAskedDate(year, month, day)) {
      nextExecutionTimes.push(date);
    }

    // Move to the next year
    year++;
  }

  return nextExecutionTimes;
}

/**
 * Calculates the next monthly execution times based on the provided day, hour, minute, and count.
 * @param {number} _day - The day of month value.
 * @param {number} _hour - The hour value.
 * @param {number} _minute - The minute value.
 * @param {number} count - The number of execution times to calculate.
 * @returns {Date[]} - An array of Date objects representing the next monthly execution times.
 */
export function calculateMonthlyExecutionTimes(_day, _hour, _minute, count) {
  // Sanitize the day, hour, and minute values.
  const [minute, hour, day] = sanitizeArgs(_minute, _hour, _day);

  // Array to store the next execution times.
  const nextExecutionTimes = [];

  // Get the current date and time.
  const now = new Date(Date.now());

  // Initialize the monthly year and month to the current date.
  let year = now.getFullYear();
  let month = now.getMonth() + 1;

  // Calculate the next execution times until the desired count is reached.
  while (nextExecutionTimes.length < count) {
    // Create a new Date object for the current monthly execution time.
    const date = new Date(year, month - 1, day, hour, minute);

    // Check if the execution time is greater than or equal to the current time
    // and if it forms a valid date.
    if (date >= now && isAskedDate(year, month, day)) {
      nextExecutionTimes.push(date);
    }

    // Move to the next month.
    month++;

    // If the next month is greater than 12, reset the month and increment the year.
    if (month > 12) {
      month = 1;
      year++;
    }
  }

  return nextExecutionTimes;
}

/**
 * Calculates the next daily execution times based on the provided hour, minute, and count.
 * @param {number} _hour - The hour value.
 * @param {number} _minute - The minute value.
 * @param {number} count - The number of execution times to calculate.
 * @returns {Date[]} - An array of Date objects representing the next daily execution times.
 */
export function calculateDailyExecutionTimes(_hour, _minute, count) {
  // Sanitize the hour and minute values.
  const [minute, hour] = sanitizeArgs(_minute, _hour);

  // Array to store the next execution times.
  const nextExecutionTimes = [];

  // Get the current date and time.
  const now = new Date(Date.now());

  // Initialize the daily year, month, and day to the current date.
  let year = now.getFullYear();
  let month = now.getMonth() + 1;
  let day = now.getDate();

  // Calculate the next execution times until the desired count is reached.
  while (nextExecutionTimes.length < count) {
    // Create a new Date object for the current daily execution time.
    const date = new Date(year, month - 1, day, hour, minute);

    // Check if the execution time is greater than or equal to the current time
    // and if it forms a valid date.
    if (date >= now && isAskedDate(year, month, day)) {
      nextExecutionTimes.push(date);
    }

    // Move to the next day.
    day++;

    // If the next day is not a valid date, reset the day, increment the month,
    // and adjust the year if necessary.
    if (!isAskedDate(year, month, day)) {
      day = 1;
      month++;

      if (month > 12) {
        month = 1;
        year++;
      }
    }
  }

  return nextExecutionTimes;
}

/**
 * Calculates the next hourly execution times based on the provided hour, minute, and count.
 * @param {number} _minute - The minute value.
 * @param {number} count - The number of execution times to calculate.
 * @returns {Date[]} - An array of Date objects representing the next hourly execution times.
 */
export function calculateHourlyExecutionTimes(_minute, count) {
  // Sanitize the hour and minute values.
  const [minute] = sanitizeArgs(_minute);

  // Array to store the next execution times.
  const nextExecutionTimes = [];

  // Get the current date and time.
  const now = new Date(Date.now());

  // Initialize the hourly year, month, day, and hour to the current date.
  let year = now.getFullYear();
  let month = now.getMonth() + 1;
  let day = now.getDate();
  let hour = now.getHours();

  // Calculate the next execution times until the desired count is reached.
  while (nextExecutionTimes.length < count) {
    // Create a new Date object for the current hourly execution time.
    const date = new Date(year, month - 1, day, hour, minute);

    // Check if the execution time is greater than or equal to the current time
    // and if it forms a valid date.
    if (date >= now && isAskedDate(year, month, day)) {
      nextExecutionTimes.push(date);
    }

    // Move to the next hour.
    hour++;

    // If the next hour is not a valid hour, reset the hour, increment the day,
    // and adjust the month and year if necessary.
    if (hour >= 24) {
      hour = 0;
      day++;

      if (!isAskedDate(year, month, day)) {
        day = 1;
        month++;

        if (month > 12) {
          month = 1;
          year++;
        }
      }
    }
  }

  return nextExecutionTimes;
}

/**
 * Sanitizes the arguments for minute, hour, day, and month.
 * @param {string} minute - The minute value.
 * @param {string} hour - The hour value.
 * @param {string} day - The day value.
 * @param {string} month - The month value.
 * @returns {Array} - An array of sanitized values for minute, hour, day, and month.
 */
export function sanitizeArgs(minute, hour, day, month) {
  // Parse the month, day, hour, and minute values into integers.
  const parsedMonth = parseInt(month, 10);
  const parsedDay = parseInt(day, 10);
  const parsedHour = parseInt(hour, 10);
  const parsedMinute = parseInt(minute, 10);

  // Sanitize the values to ensure they fall within valid ranges.
  // If a value is outside the valid range, set it to the default value.
  return [
    Math.max(0, Math.min(parsedMinute, 59)) || 0, // Sanitize the minute value (range: 0-59, default: 0).
    Math.max(0, Math.min(parsedHour, 23)) || 0, // Sanitize the hour value (range: 0-23, default: 0).
    Math.max(1, Math.min(parsedDay, 31)) || 1, // Sanitize the day value (range: 1-31, default: 1).
    Math.max(1, Math.min(parsedMonth, 12)) || 1, // Sanitize the month value (range: 1-12, default: 1).
  ];
}

/**
 * Checks if the provided year, month, and day form a valid date.
 * @param {number} year - The year value.
 * @param {number} month - The month value (1-12).
 * @param {number} day - The day value.
 * @returns {boolean} - True if the provided values form a valid date; otherwise, false.
 */
export function isAskedDate(year, month, day) {
  // Create a new Date object using the provided year, month, and day.
  // Note: JavaScript months are zero-based, so we subtract 1 from the month value.
  const date = new Date(year, month - 1, day);

  // Check if the year, month, and day of the created date match the provided values.
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}
